import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import Button from '../../../../component/button';
import { ApiLink, Content, Description, Details, First, Heading, Icons } from './styles';
import AddApiKeyModal from '../../../../component/modals/addApiKeyModal';
import { deleteApiKey, getApiKeyList } from '../../../../services/settingServices';
import copy from 'clipboard-copy';
import Modal from '../../../../component/modals/modal';
import DeleteModal from '../../../../component/modals/deleteModal';
import ApiKeyLoading from '../../../../component/loading/apiKeyLoading';
import { captureException } from '../../../../services/logService';
import { isEmpty } from '../../../../helpers/common';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import { rootStore } from '../../../../mobx/rootStore';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import { BUTTON_TYPE, EMPTY_STATE_SIZE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import { Group, Header, PersonalText, Container } from '../../styles';
import IconButton from '../../../../component/button/iconButton';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../../../component/emptyState';
interface ApiKeyListInterface {
  _id: string;
  title: string;
  apiKey: string;
}

const ApiKeys: React.FC = () => {
  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItemID, setSelectedItemId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [apiKeyList, setApiKeyList] = useState<ApiKeyListInterface[]>([]);

  //mobx states
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const isMobile = useMobileDevice();

  // Opens the modal for creating a new API key
  const handleCreateApiKey = () => {
    setIsModalOpen(true);
  };

  // Closes the create API key modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Loads the list of API keys for the current workspace
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!isEmpty(currentWorkspace?.id)) {
        const response = await getApiKeyList();
        setApiKeyList(response || []);
      }
    } catch (error) {
      captureException(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  // Load API keys when component mounts
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Opens the delete confirmation modal
  const OpenDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  // Closes the delete confirmation modal
  const onCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  // Copies the API key to clipboard
  const handleCopyApiKey = useCallback(async (text: string) => {
    try {
      await copy(text);
      rootStore.alertNotificationStore.setSuccessMessage('API key copied successfully!');
    } catch (error) {
      captureException(error);
    }
  }, []);

  // Deletes an API key
  const deleteAPIKey = useCallback(
    async (id: string) => {
      try {
        setDeleteLoading(true);
        const result = await deleteApiKey(id);
        if (result) {
          setDeleteModalOpen(false);
          loadData();
        }
      } catch (error) {
        captureException(error);
        return;
      } finally {
        setDeleteLoading(false);
        setIsModalOpen(false);
      }
    },
    [loadData]
  );

  return (
    <>
      <AppLayout isPosition={true}>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <Header>
                <PersonalText>API Keys</PersonalText>
                <Button
                  title='Create Key'
                  variant={BUTTON_TYPE.PRIMARY}
                  buttonType='button'
                  onClick={handleCreateApiKey}
                  size='small'
                />
              </Header>
            ) : (
              <ResponsiveHeader title='API Keys' />
            )}
            {loading && <ApiKeyLoading />}
            {!loading && apiKeyList?.length === 0 && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title='No API keys created yet'
                description='Get started by creating your first API key.'
                isAlignedCenter={true}
                imageUrl='api_empty'
              />
            )}

            {!loading && apiKeyList?.length > 0 && (
              <>
                <Content>
                  {apiKeyList?.map((item) => {
                    return (
                      <First key={item['_id']}>
                        <Details>
                          <Heading>{item?.title}</Heading>
                          <Description>**** ***** **** {item?.apiKey?.slice(-4)}</Description>
                        </Details>
                        <Icons>
                          <IconButton
                            iconName='copy-icon'
                            size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.SMALL}
                            variant={ICON_BUTTON_TYPE.TERTIARY}
                            onClick={() => handleCopyApiKey(item?.apiKey)}
                          />
                          <Dropdown
                            modalTitle='Manage API Key'
                            activeClassName='active'
                            content={
                              <IconButton
                                iconName='three-dots-horizontal-icon'
                                size={isMobile ? ICON_BUTTON_SIZE.LARGE : ICON_BUTTON_SIZE.SMALL}
                                variant={ICON_BUTTON_TYPE.TERTIARY}
                                onClick={() => setSelectedItemId(item['_id'])}
                              />
                            }
                            trigger='click'>
                            <DropdownItem label='Delete' tone='critical' onClick={() => OpenDeleteModal()} />
                          </Dropdown>
                        </Icons>
                      </First>
                    );
                  })}
                </Content>
                <ApiLink>
                  Explore our
                  <a href='https://api.teamcamp.app' target='_blank' rel='noreferrer'>
                    API Doc
                  </a>
                </ApiLink>
              </>
            )}
          </Group>
        </Container>
      </AppLayout>
      <Modal open={isModalOpen} onClose={handleCloseModal} maxWidth={'462px'}>
        <AddApiKeyModal onCancel={handleCloseModal} loadData={loadData} />
      </Modal>
      <Modal open={deleteModalOpen} onClose={onCloseDeleteModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={onCloseDeleteModal}
          onOk={() => deleteAPIKey(selectedItemID)}
          loading={deleteLoading}
          modalTitle='Delete Api key?'
          description='Are you sure you want to delete this api key?'
        />
      </Modal>
      <BottomSubNavigation rightElement={<BottomNavigationItem iconName='plus-icon' onClick={handleCreateApiKey} />} />
    </>
  );
};
export default observer(ApiKeys);
